import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiPaths } from "../../../service/apiPaths";
import ApiService from "../../../service/ApiService";
import {
  formatDateToISODateString,
  updateComponent,
  ViewType,
} from "../../../util/constant";
import { ITaskSheet } from "./Tasksheet.props";

const useTaskSheet = () => {
  const [selectedTaskSheetId, setSelectedTaskSheetId] = useState<string | null>(
    null
  );
  const [taskSheetData, setTaskSheetData] = useState<ITaskSheet[]>([]);
  const [yesterDayData, setYesterDayData] = useState<ITaskSheet[]>([]);
  const [tomorrowDayData, setTomorrowDayData] = useState<ITaskSheet[]>([]);
  const [backLogData, setBackLogData] = useState<ITaskSheet[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [yesterPage, setYesterPage] = useState<number>(1);
  const [tomorrowPage, setTomorrowPage] = useState<number>(1);
  const [backPage, setBackPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [yesterDayPage, setYesterDayPage] = useState<number>(1);
  const [tomorrowDayPage, setTomorrowDayPage] = useState<number>(1);
  const [backLogPage, setBackLogPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [description, setDescription] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedId, setSelectedId] = useState<any>();
  const [adminComment, setAdminComment] = useState<any[]>([]);
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);
  const [latestAdminCommentId, setLatestAdminCommentId] = useState<string | null>(null);
  const [latestAdminComment, setLatestAdminComment] =
    useState<string>("No comment");
  const [welcomeMessageSent, setWelcomeMessageSent] = useState(false);

  const [taskSummary, setTaskSummary] = useState<any>({
    user: { name: "", role: "", email: "" },
    overall: {
      todo: 0,
      inProgress: 0,
      query: 0,
      finished: 0,
      backLog: 0,
      totalTasks: 0,
    },
    today: {
      todo: 0,
      inProgress: 0,
      query: 0,
      finished: 0,
      backLog: 0,
      totalTasks: 0,
    },
  });
  const limit = 5;
  const navigate = useNavigate();

  // Check if it's the first access of the day
  const checkFirstAccessOfDay = () => {
    const lastAccessDate = localStorage.getItem("lastAccessDate");
    const today = new Date().toISOString().split("T")[0];

    if (lastAccessDate !== today) {
      localStorage.setItem("lastAccessDate", today);
      return true;
    }
    return false;
  };

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint = `${apiPaths.getAllEmpTaskSheets}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${page}&limit=${limit}`;
        if (search) {
          endpoint = `${apiPaths.getAllEmpTaskSheets}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&search=${search}&page=${page}&limit=${limit}`;
        }
      } else {
        endpoint = `${apiPaths.getAllEmpTaskSheets}?page=${page}&limit=${limit}`;
        if (search) {
          endpoint = `${apiPaths.getAllEmpTaskSheets}?search=${search}&page=${page}&limit=${limit}`;
        }
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setTaskSheetData(result.data);

      setTotalPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const buildEndpoint = (
    pageNumber: number,
    fromDate?: Date | null,
    toDate?: Date | null,
    pageLimit: number = limit
  ) => {
    let endpoint = `${apiPaths.getAllEmpTaskSheets}?page=${pageNumber}&limit=${pageLimit}`;

    if (fromDate && toDate) {
      const from = formatDateToISODateString(fromDate);
      const to = formatDateToISODateString(toDate);
      endpoint += `&fromDate=${from}&toDate=${to}`;
    }

    if (search) {
      endpoint += `&search=${search}`;
    }

    return endpoint;
  };

  const fetchAllTaskSheets = async (
    fromDate?: Date | null,
    toDate?: Date | null
  ) => {
    let currentPage = 1;
    let allData: any[] = [];
    let totalPages = 1;
    const bulkLimit = 100;

    do {
      const endpoint = buildEndpoint(currentPage, fromDate, toDate, bulkLimit);

      const result = await ApiService({
        method: "GET",
        endpoint,
      });

      allData = [...allData, ...(result.data || [])];
      totalPages = result.totalPages || 1;
      currentPage++;
    } while (currentPage <= totalPages);

    return allData;
  };

  const handleTomorrowDay = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint = `${apiPaths.getUpComingTaskSheet}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${tomorrowPage}&limit=${limit}`;
      } else {
        endpoint = `${apiPaths.getUpComingTaskSheet}?page=${tomorrowPage}&limit=${limit}`;
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setTomorrowDayData(result.data);
      setTomorrowDayPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleYesterDay = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint = `${apiPaths.getPendingTaskSheet}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${yesterPage}&limit=${limit}`;
      } else {
        endpoint = `${apiPaths.getPendingTaskSheet}?page=${yesterPage}&limit=${limit}`;
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setYesterDayData(result.data);
      setYesterDayPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBackLog = async (fromDate: Date | null, toDate: Date | null) => {
    console.log("handleBackLog");
    setLoading(true);
    try {
      let endpoint = `${apiPaths.getBackLogTaskSheet}?page=${backPage}&limit=${limit}`;
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint = `${apiPaths.getBackLogTaskSheet}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${backPage}&limit=${limit}`;
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setBackLogData(result.data);
      setBackLogPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTaskSummary = async () => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: apiPaths.taskSummary,
      });
      setTaskSummary(result);
    } catch (error) {
      console.error("Error fetching task summary:", error);
    }
  };
 
  const normalizeCommentTime = (t: string | Date) =>
    new Date(t).toISOString();

  const handleAdminComment = async () => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: apiPaths.getAdminComment,
      });

      const comments = Array.isArray(result)
        ? result
        : result?.comments ?? [];
      const lastAcknowledgedAdminCommentAt: string | null =
        Array.isArray(result) ? null : result?.lastAcknowledgedAdminCommentAt ?? null;

      setAdminComment(comments);

      if (!comments || comments.length === 0) {
        setShowWelcomeModal(false);
        return;
      }

      const latestComment = comments[0];
      const uniqueCommentId = normalizeCommentTime(latestComment.commentTime);

      setLatestAdminComment(latestComment.comments);
      setLatestAdminCommentId(uniqueCommentId);

      const lastAck = lastAcknowledgedAdminCommentAt
        ? normalizeCommentTime(lastAcknowledgedAdminCommentAt)
        : null;

      if (lastAck === uniqueCommentId) {
        setShowWelcomeModal(false);
        setWelcomeMessageSent(true);
        return;
      }

      setWelcomeMessageSent(false);
      setShowWelcomeModal(true);
    } catch (error) {
      console.error("Error fetching admin comments:", error);
      setShowWelcomeModal(false);
    }
  };
  
  useEffect(() => {
    handleAdminComment();
    fetchTaskSummary();
  }, []);
  
  
  const handleEdit = (id: string) => {
    setSelectedTaskSheetId(id);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };
  
  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addTaskSheet");
  };
  
  const updateScreen = (type: ViewType) => {
    updateComponent(type, () => {}, setSelectedTaskSheetId);
  };

  const handleSelectStatus = (e: any, id: any) => {
    const status = e.target.value;
    if (status === "query" || status === "backLog") {
      setShowModal(true);
      setSelectedStatus(status);
      setSelectedId(id);
    } else {
      handleConfirmStatusUp(id, status, "");
    }
  };

  const handleModalSubmit = () => {
    if (selectedStatus) {
      handleConfirmStatusUp(selectedId, selectedStatus, description);
    }
    setShowModal(false);
    setDescription("");
  };

  const handleModalClose = () => {
    setShowModal(false);
    setDescription("");
  };

  const sendSlackMessage = async (id: any) => {
    setShowModal(true);
    try {
      const slackMessage = {
        text: `---\n👩‍🎨User: ${id.assignedTo.name} \n\nProject: ${id.projectId.name}.\nTask: ${id.description} \n🔹 Note: ${description} `,
      };

      await ApiService({
        method: "POST",
        endpoint: `${apiPaths.sendSlackMessage}`,
        data: slackMessage,
      });
    } catch (error) {
      console.error(error);
    }
  };
  
  const handleConfirmStatusUp = async (
    id: any,
    status: any,
    description: string
  ) => {
    console.log("id", id);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setLoading(true);
    const queryData = {
      status: status,
      query: description,
    };
    const backLogData = {
      status: status,
      backLog: description,
    };
    const progressData = {
      status: status,
      dueDate: today,
    };
    const finishedData = {
      status: status,
      userVideoUrl: id.userVideoUrl || id.videoUrl,
    };

    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateTaskSheet}${id._id}`,
        data:
          status === "query"
            ? queryData
            : status === "in progress"
            ? progressData
            : status === "finished"
            ? finishedData
            : backLogData,
      });

      // Send a message to Slack if the status is "query" or "backLog"
      if (status === "query" || status === "backLog") {
        const slackMessage = {
          text: `---\n👩‍🎨User: ${id.assignedTo.name} \n❌ Status: ${status} \n\nProject: ${id.projectId.name}.\nTask: ${id.description} \n🔹 Note: ${description} `,
        };

        await ApiService({
          method: "POST",
          endpoint: `${apiPaths.sendSlackMessage}`,
          data: slackMessage,
        });
      }

      // Optionally refresh all tables
      handleDateFilter(today, today);
      handleYesterDay(null, null);
      handleBackLog(null, null);
      handleTomorrowDay(tomorrow, tomorrow);
    } catch (error) {
      console.error("Error updating task status:", error);
    } finally {
      setLoading(false);
    }
  };

  return {
    selectedTaskSheetId,
    taskSheetData,
    showConfirmation,
    handleAdd,
    handleEdit,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    handleYesterDay,
    handleBackLog,
    handleSelectStatus,
    handleTomorrowDay,
    updateScreen,
    fetchAllTaskSheets,
    loading,
    setSearch,
    search,
    setPage,
    setYesterPage,
    setBackPage,
    setTomorrowPage,
    page,
    yesterPage,
    backPage,
    tomorrowPage,
    totalPage,
    yesterDayData,
    backLogData,
    yesterDayPage,
    tomorrowDayData,
    tomorrowDayPage,
    backLogPage,
    showModal,
    description,
    setDescription,
    handleModalSubmit,
    handleModalClose,
    adminComment,
    taskSummary,
    sendSlackMessage,
    setShowModal,
    selectedId,
    setSelectedId,
    showWelcomeModal,
    setShowWelcomeModal,
    latestAdminComment,
    welcomeMessageSent,
    setWelcomeMessageSent,
    latestAdminCommentId,
    setLatestAdminCommentId,
  };
};

export default useTaskSheet;